import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppNotification } from './notification.model';
import {KeycloakService} from "keycloak-angular";

/**
 * SignalR client for the institution-portal notification hub.
 *
 * Hub:    /institution-portal-notification-hub (under the assessment service)
 * Group:  institution-portal-[institutionUserId]  (institutionUserId = Keycloak account id)
 * Event:  StudentsImportFeedbackReceived (payload: string)
 *
 * Modeled on WebSocketsService (payment hub): the connection carries the
 * institution user id as a query param and the server joins it to its group.
 */
@Injectable({
  providedIn: 'root',
})
export class InstitutionNotificationService {
  hubConnection?: signalR.HubConnection;

  private importFeedbackSubject = new Subject<AppNotification>();
  /** Emits each newly received notification (drives the header toast/badge). */
  importFeedback$ = this.importFeedbackSubject.asObservable();

  constructor(private keycloakService: KeycloakService) {}

  /** Opens the hub connection for the given institution user (connects once). */
  start(institutionUserId: string): void {
    if (!institutionUserId || this.hubConnection) {
      return;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${environment.apiConfig.institutionPortalHubUrl}?institution-user-id=${institutionUserId}`,
        {
          withCredentials: true,
          accessTokenFactory: () => this.keycloakService.getToken()
        }
      )
      .withAutomaticReconnect([0, 0, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.hubConnection.on('JoinedGroup', (message: string) => {
      console.log('Institution notification hub:', message);
    });

    this.hubConnection.on(
      'StudentsImportFeedbackReceived',
      (message: string) => {
        const notification: AppNotification = {
          id: `import-${new Date().getTime()}`,
          message,
          api: '',
          read: false,
          timestamp: new Date(),
        };
        this.importFeedbackSubject.next(notification);
      }
    );

    this.hubConnection
      .start()
      .then(() =>
        console.log('Institution Portal Notification Connection Established')
      )
      .catch((err) => console.error('SignalR Error:', err));
  }

  /** Closes the hub connection (e.g. on logout / header teardown). */
  stop(): void {
    this.hubConnection?.stop().catch(() => {});
    this.hubConnection = undefined;
  }
}
